import React, { useEffect, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'

import {
  EXTERNAL_URLS, PHONE_INFO,
} from '@/common/constants'
import {
  useFormFieldTracking, useParseApiErrors, usePassword, useSubmitForm,
} from '@/common/hooks'
import { isEmpty } from '@/common/utils'
import { joinLeadSchema } from '@/common/validations'
import AnimateIn from '@components/AnimateIn'
import Button, { SIGN_TYPES } from '@components/Button'
import FormCheckbox from '@components/FormCheckbox'
import FormInput, { FORM_INPUT_TYPES } from '@components/FormInput'
import FormPassword from '@components/FormPassword'
import HiddenPanel from '@components/HiddenPanel'

const LeadEnrollmentForm = ({
  apiErrors = null,
  loadPasswordField = true,
  onSubmit,
  prefill = {},
  loadPhoneNumber = true,
  teamName = 'myFloc',
}) => {
  if (!onSubmit || typeof onSubmit !== 'function') {
    throw new Error('form submission handler must be a function')
  }

  const { passwordErrors, passwordSchema, validatePassword } = usePassword()
  const { passwordErrors: password2Errors, validatePassword: validatePassword2 } = usePassword()
  const { showValidationToast } = useSubmitForm()

  const [showPhoneInfo, setShowPhoneInfo] = useState(false)
  const [showDisplayName, setDisplayName] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const doOnSubmit = async formFields => {
    if (isSubmitting) return
    setIsSubmitting(true)
    try {
      await onSubmit(formFields)
    }
    catch (submitError) {
      console.error('error submitting form:', submitError)
    }

    setIsSubmitting(false)
  }

  const {
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    watch,
  } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(
      joinLeadSchema
        .concat(loadPasswordField ? passwordSchema : null)
    ),
  })
  const { reset: resetFieldTracking, updatedFields: fieldsUpdatedSinceSubmit } = useFormFieldTracking(watch)
  const { apiValidationErrors } = useParseApiErrors(apiErrors, { filter: fieldsUpdatedSinceSubmit })

  // prefill form fields
  useEffect(() => {
    // prefill non-nested fields
    Object.keys(prefill)
      .forEach(fieldName => {
        if (typeof prefill[fieldName] !== 'object' && prefill[fieldName] !== null) {
          setValue(fieldName, prefill[fieldName])
        }
      })
  }, [prefill])

  // reset updated fields list on form submission
  useEffect(() => {
    if (isSubmitting && fieldsUpdatedSinceSubmit.length > 0) resetFieldTracking()
  }, [isSubmitting, fieldsUpdatedSinceSubmit, resetFieldTracking])

  return (
    <form
      onSubmit={handleSubmit(doOnSubmit, showValidationToast)}
    >
      <div className='text-sm'>*Required field</div>
      <div className='mt-1 text-lg'>Please enter the following information to join {teamName}</div>

      {/* Personal Info */}
      <div className='flex flex-row justify-between mt-6'>
        <FormInput
          autoComplete='given-name'
          errors={errors}
          hintContent='Use full legal name.'
          label='First Name'
          name='firstName'
          register={register}
          validationErrors={apiValidationErrors}
          required
        />
        <div className='mx-2' />
        <FormInput
          autoComplete='middle-name'
          errors={errors}
          label='Middle (optional)'
          name='middleName'
          register={register}
          validationErrors={apiValidationErrors}
        />
      </div>
      <FormInput
        autoComplete='family-name'
        errors={errors}
        label='Last Name'
        name='lastName'
        register={register}
        validationErrors={apiValidationErrors}
        required
      />
      <AnimateIn maxHeight={121} show={showDisplayName}>
        <FormInput
          errors={errors}
          label='Preferred First Name (Optional)'
          name='displayName'
          register={register}
          validationErrors={apiValidationErrors}
        />
      </AnimateIn>

      <Button
        className='mb-2 flat-left'
        onClick={() => setDisplayName(!showDisplayName)}
        sign={showDisplayName ? SIGN_TYPES.minus : SIGN_TYPES.plus}
        type='button'
        primary
        text
      >
        {showDisplayName ? 'Hide Preferred First Name' : 'Add Preferred First Name (Optional)'}
      </Button>

      <FormInput
        autoComplete='email'
        className='my-7'
        errors={errors}
        hintContent='You will use this email address when logging in to myFloc.'
        label='Email Address'
        name='email'
        register={register}
        type={FORM_INPUT_TYPES.email}
        validationErrors={apiValidationErrors}
        required
      />

      {loadPhoneNumber ? (
        <>
          <FormInput
            autoComplete='tel'
            errors={errors}
            // inputClass='w-60'
            label='Cell Phone Number (optional)'
            labelButtonAction={() => setShowPhoneInfo(true)}
            labelButtonText='Why Provide This?'
            name='mobile'
            register={register}
            type={FORM_INPUT_TYPES.phone}
            validationErrors={apiValidationErrors}
          />

          <HiddenPanel setShow={setShowPhoneInfo} show={showPhoneInfo}>
            {PHONE_INFO}
          </HiddenPanel>

          <FormCheckbox
            className='mb-0'
            id='smsConsent'
            name='smsConsent'
            register={register}
            subtitle='Phone provider message and data rates may apply.'
            title='Allow Text Message Notifications'
            plain
          />
        </>
      ) : null}

      {/* Password + Validation */}
      {loadPasswordField ? (
        <>
          <FormPassword
            apiErrors={apiErrors}
            errors={errors}
            passwordErrors={passwordErrors}
            register={register}
            validatePassword={validatePassword}
          />
          <FormPassword
            apiErrors={apiErrors}
            className='mt-10'
            controlName='password2'
            errors={errors}
            passwordErrors={password2Errors}
            register={register}
            validatePassword={validatePassword2}
          />
        </>
      ) : null}

      <FormCheckbox
        className='mb-8 min-h-0 mt-10'
        errors={errors}
        id='myflocAgreements'
        name='myflocAgreements'
        register={register}
        validationErrors={apiValidationErrors}
        plain
      >
        <div className='text-base font-bold'>
          I agree to myFloc's{' '}
          <Button
            className='p-0 m-0 font-bold flat-x'
            href={EXTERNAL_URLS.TERMS_AND_CONDITIONS}
            rel='noreferrer'
            target='_blank'
            primary
            text
          >
            Terms and Conditions
          </Button>{' '}
          and{' '}
          <Button
            className='p-0 m-0 font-bold flat-x'
            href={EXTERNAL_URLS.PRIVACY_POLICY}
            rel='noreferrer'
            target='_blank'
            primary
            text
          >
            Privacy Policy
          </Button>
          .
        </div>
      </FormCheckbox>

      <div className='flex flex-row justify-center items-center mt-9'>
        <Button
          disabled={
            !watch(['myflocAgreements'])
              .every(
                x => x
              ) ||
            !isEmpty(errors) ||
            !isEmpty(apiValidationErrors)
          }
          isLoading={isSubmitting}
        >
          Join myFloc
        </Button>
      </div>
    </form>
  )
}

LeadEnrollmentForm.propTypes = {
  apiErrors: PropTypes.object,
  loadPasswordField: PropTypes.bool,
  loadPhoneNumber: PropTypes.bool,
  loadShippingFields: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  prefill: PropTypes.object,
  teamName: PropTypes.string,
  textMessageNotificationsField: PropTypes.bool,
}

export default LeadEnrollmentForm
